import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { TrendingUp, Users, Package, Percent, DollarSign, Layers, Table2, Radio, MessageSquarePlus, UserPlus, ListChecks, MapPin, Filter, Activity, Gauge, XCircle, CreditCard, AlertTriangle } from "lucide-react";
import { GatedPill, centsToUsd, Tile, CountTile, BarList, ExportButton, EmptyNote } from "./shared";

// A labeled section: heading + one-line description on the left, a right-aligned scope note
// (the window or basis) on the right. Gives the page a document rhythm instead of a pile of panels.
function Section({ title, desc, scope, icon: Icon, children, testId }) {
  return (
    <section className="mt-12 first:mt-0" data-testid={testId}>
      <div className="flex items-end justify-between flex-wrap gap-2 border-b border-sniper pb-2 mb-4">
        <div>
          <h2 className="font-heading text-2xl uppercase tracking-tight text-foreground flex items-center gap-2">
            {Icon && <Icon className="w-5 h-5 text-olive-bright" strokeWidth={1.7} />} {title}
          </h2>
          {desc && <p className="text-[11px] font-mono text-muted-foreground mt-1 max-w-2xl leading-snug">{desc}</p>}
        </div>
        {scope && <span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground whitespace-nowrap">{scope}</span>}
      </div>
      {children}
    </section>
  );
}

function StateChip({ state }) {
  if (state === "enabled")
    return <span data-testid="fee-state-enabled" className="font-mono text-[10px] uppercase tracking-wide border border-olive-bright/50 text-olive-bright px-1.5 py-0.5">Enabled</span>;
  return <span data-testid="fee-state-off" className="font-mono text-[10px] uppercase tracking-wide border border-sniper text-muted-foreground px-1.5 py-0.5">Configured · Off</span>;
}

function DeltaCell({ pct }) {
  if (pct == null) return <span className="text-muted-foreground">—</span>;
  const up = pct >= 0;
  return <span className={up ? "text-olive-bright" : "text-blaze"}>{up ? "+" : ""}{pct}%</span>;
}

// heat-map cell background by conversion intensity (low -> high)
function heatBg(pct) {
  if (pct == null) return "transparent";
  const t = Math.min(pct, 100) / 100;
  return `rgba(124, 158, 45, ${0.12 + t * 0.75})`;
}

const offsetLabel = (i) => (i === 0 ? "Signup month" : `${i} month${i === 1 ? "" : "s"} later`);

export default function MarketplaceTab({ force }) {
  const [kpis, setKpis] = useState(null);
  const [series, setSeries] = useState(null);
  const [fees, setFees] = useState(null);
  const [credits, setCredits] = useState(null);
  const [pl, setPl] = useState(null);
  const [cohorts, setCohorts] = useState(null);
  const [pulse, setPulse] = useState(null);
  const [ua, setUa] = useState(null);
  const [cohortMetric, setCohortMetric] = useState("first_listing");

  useEffect(() => {
    const q = force ? "?_force_refresh=1" : "";
    Promise.all([
      api.get(`/admin/analytics/kpis${q}`),
      api.get(`/admin/analytics/series${q}`),
      api.get(`/admin/analytics/fees-breakdown${q}`),
      api.get(`/admin/analytics/credits-summary${q}`),
      api.get(`/admin/analytics/pl-statement${q}`),
      api.get(`/admin/analytics/beta-pulse${q}`),
      api.get(`/admin/analytics/user-analytics${q}`),
    ]).then(([k, s, f, c, p, bp, u]) => {
      setKpis(k.data); setSeries(s.data); setFees(f.data); setCredits(c.data); setPl(p.data); setPulse(bp.data); setUa(u.data);
    }).catch(() => {});
  }, [force]);

  useEffect(() => {
    const sep = force ? "&_force_refresh=1" : "";
    api.get(`/admin/analytics/cohorts?metric=${cohortMetric}${sep}`).then(({ data }) => setCohorts(data)).catch(() => {});
  }, [cohortMetric, force]);

  const revenueGated = series?.revenue_display_state === "stripe_gated";
  const chartData = (series?.points || []).map((p) => ({
    date: p.date.slice(5),
    gmv: p.gmv_cents / 100,
    revenue: p.revenue_cents == null ? null : p.revenue_cents / 100,
  }));

  const cohortCount = (cohorts?.rows || []).length;
  const cohortTotalUsers = (cohorts?.rows || []).reduce((a, r) => a + (r.size || 0), 0);

  return (
    <div data-testid="marketplace-tab">
      {/* 1 — MARKETPLACE KPIs */}
      <Section title="Marketplace" testId="section-marketplace"
        icon={TrendingUp}
        desc="Live marketplace health. Counts are self-reported; payment-dependent figures stay gated until Stripe settlement is wired."
        scope="Excludes test + demo · first-party (not GA4)">
        {kpis && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="kpi-row-1">
              <Tile icon={TrendingUp} label="Gross Merchandise Value (Month to Date)" tile={kpis.gmv_mtd_cents} />
              <Tile icon={DollarSign} label="Revenue (Month to Date)" tile={kpis.revenue_mtd_cents} />
              <Tile icon={DollarSign} label="Revenue (7 days)" tile={kpis.revenue_7d_cents} />
              <Tile icon={DollarSign} label="Average Sale Price (30 days)" tile={kpis.avg_sale_price_cents} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3" data-testid="kpi-row-2">
              <CountTile icon={Package} label="Active Listings" value={kpis.active_listings?.value} />
              <CountTile icon={Users} label="New Signups (30 days)" value={kpis.new_signups_30d?.value} />
              <CountTile icon={Users} label="Active Sellers (30 days)" value={kpis.active_sellers_30d?.value} />
              <Tile icon={Percent} label="Conversion (30 days)" tile={kpis.listing_to_sale_conversion_pct_30d} isPct />
            </div>
          </>
        )}
      </Section>

      {/* 2 — GMV CHART */}
      <Section title="Gross Merchandise Value + Revenue" testId="section-gmv"
        icon={Activity}
        desc="Gross Merchandise Value is live from self-reported sales. The revenue overlay activates with payments."
        scope="Materialized nightly · 60 days">
        <div className="border border-sniper bg-surface p-4" data-testid="series-chart">
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#2A2E2A" vertical={false} />
              <XAxis dataKey="date" stroke="#5C605C" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} interval={9} />
              <YAxis stroke="#5C605C" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} />
              <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #2A2E2A", borderRadius: 0, fontFamily: "JetBrains Mono", fontSize: 12 }} formatter={(v, n) => [`$${v}`, n === "gmv" ? "Gross Merchandise Value" : "Revenue"]} />
              <Legend wrapperStyle={{ fontFamily: "JetBrains Mono", fontSize: 11, textTransform: "uppercase" }} />
              <Line type="monotone" dataKey="gmv" name="Gross Merchandise Value" stroke="#CCFF00" strokeWidth={2} dot={false} />
              {!revenueGated && <Line type="monotone" dataKey="revenue" name="Revenue" stroke="#7C9E2D" strokeWidth={2} dot={false} />}
            </LineChart>
          </ResponsiveContainer>
          {revenueGated && <div className="mt-2 flex justify-end"><GatedPill note="Revenue overlay activates with payments" /></div>}
        </div>
      </Section>

      {/* 3 — PLATFORM FEES */}
      <Section title="Platform Fees" testId="section-fees"
        icon={DollarSign}
        desc="Every fee, its basis, and whether it is switched on. A fee that exists but is off is shown as such, not omitted."
        scope="Month to date · excludes test + demo">
        <div className="border border-sniper bg-surface" data-testid="fees-table">
          <div className="grid grid-cols-[1.6fr_1.4fr_1fr_0.6fr_1fr] gap-2 px-3 py-2 border-b border-sniper font-mono text-[10px] uppercase text-muted-foreground">
            <span>Fee</span><span>Basis</span><span>State</span><span className="text-right">Count</span><span className="text-right">Amount MTD</span>
          </div>
          {(fees?.rows || []).map((r) => (
            <div key={r.category} className="grid grid-cols-[1.6fr_1.4fr_1fr_0.6fr_1fr] gap-2 px-3 py-3 border-b border-sniper last:border-0 items-center" data-testid={`fee-row-${r.category}`}>
              <span className="font-body text-sm text-foreground">
                {r.label}
                {r.price_cents != null && <span className="block font-mono text-[10px] text-muted-foreground">{centsToUsd(r.price_cents)} each</span>}
              </span>
              <span className="font-mono text-xs text-secondary-foreground">{r.basis}</span>
              <span><StateChip state={r.state} /></span>
              <span className="font-mono text-sm text-secondary-foreground text-right">{r.state === "enabled" ? r.count : "—"}</span>
              <span className="font-mono text-sm text-right">{r.state === "enabled" ? <span className="text-volt">{centsToUsd(r.amount_cents)}</span> : <span className="text-muted-foreground">—</span>}</span>
            </div>
          ))}
        </div>
      </Section>

      {/* 4 — PROFIT & LOSS */}
      <Section title="Profit & Loss" testId="section-pl"
        icon={Layers}
        desc="Current month against the prior month. Money lines read an em dash — not $0 — until real payments produce a figure."
        scope={pl ? `${pl.current?.label} vs ${pl.prior?.label}` : ""}>
        <div className="border border-sniper bg-surface overflow-x-auto" data-testid="pl-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="font-mono text-[10px] uppercase text-muted-foreground border-b border-sniper">
                <th className="text-left p-3">Line</th>
                <th className="text-right p-3">{pl?.current?.label || "Current"}</th>
                <th className="text-right p-3">{pl?.prior?.label || "Prior"}</th>
                <th className="text-right p-3">Δ</th>
              </tr>
            </thead>
            <tbody>
              {(pl?.rows || []).map((r) => (
                <tr key={r.key} className={`border-b border-sniper last:border-0 ${r.emphasis ? "bg-olive/5" : ""}`} data-testid={`pl-row-${r.key}`}>
                  <td className={`p-3 font-body ${r.emphasis ? "text-foreground font-bold" : "text-foreground"}`}>
                    {r.label}
                    {r.awaiting && <span className="ml-2 font-mono text-[10px] uppercase text-[#d3a24e]" data-testid={`pl-awaiting-${r.key}`}>{r.awaiting}</span>}
                  </td>
                  <td className={`p-3 text-right font-mono ${r.emphasis ? "text-foreground font-bold" : "text-foreground"}`}>
                    {r.current_cents == null ? <span className="text-muted-foreground">—</span> : centsToUsd(r.current_cents)}
                  </td>
                  <td className="p-3 text-right font-mono text-secondary-foreground">
                    {r.prior_cents == null ? <span className="text-muted-foreground">—</span> : centsToUsd(r.prior_cents)}
                  </td>
                  <td className="p-3 text-right font-mono"><DeltaCell pct={r.delta_pct} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* 5 — CREDITS */}
      <Section title="Credits" testId="section-credits"
        icon={CreditCard}
        desc="Credits issued, redeemed and outstanding — plus a traceable record of every grant."
        scope="Live ledger · excludes test + demo">
        {credits && (
          <div className="grid grid-cols-3 gap-3 mb-4" data-testid="credits-tiles">
            {[["Issued", credits.mtd.issued_cents, credits.all_time.issued_cents],
              ["Redeemed", credits.mtd.redeemed_cents, credits.all_time.redeemed_cents],
              ["Outstanding", credits.mtd.outstanding_cents, credits.all_time.outstanding_cents]].map(([l, mtdV, allV]) => (
              <div key={l} className="border border-sniper bg-surface p-4" data-testid={`credit-tile-${l.toLowerCase()}`}>
                <div className="font-mono text-[10px] uppercase text-muted-foreground">{l}</div>
                <div className="font-heading text-xl text-foreground mt-1">{centsToUsd(mtdV)}<span className="text-[10px] font-mono text-muted-foreground"> month to date</span></div>
                <div className="font-mono text-[10px] text-secondary-foreground mt-0.5">{centsToUsd(allV)} all-time</div>
              </div>
            ))}
          </div>
        )}
        <p className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground mb-2">Recent grants</p>
        <div className="border border-sniper bg-surface" data-testid="credits-grants">
          <div className="grid grid-cols-[1fr_1.6fr_0.8fr_0.9fr] gap-2 px-3 py-2 border-b border-sniper font-mono text-[10px] uppercase text-muted-foreground">
            <span>Account</span><span>Reason</span><span className="text-right">Amount</span><span className="text-right">Date</span>
          </div>
          {(credits?.recent_grants || []).length === 0 ? (
            <EmptyNote>No credit grants recorded yet — grants will appear here with the account, reason, amount and date.</EmptyNote>
          ) : (
            (credits.recent_grants || []).map((g) => (
              <div key={g.entry_id} className="grid grid-cols-[1fr_1.6fr_0.8fr_0.9fr] gap-2 px-3 py-2.5 border-b border-sniper last:border-0 items-center text-sm" data-testid={`credit-grant-${g.entry_id}`}>
                <span className="font-mono text-xs text-foreground truncate">{g.username ? `@${g.username}` : "—"}</span>
                <span className="font-body text-secondary-foreground truncate">{g.reason || "—"}</span>
                <span className="font-mono text-olive-bright text-right">{centsToUsd(g.amount_cents)}</span>
                <span className="font-mono text-xs text-muted-foreground text-right">{(g.created_at || "").slice(0, 10)}</span>
              </div>
            ))
          )}
        </div>
      </Section>

      {/* 6 — SIGNUP FUNNEL */}
      <Section title="Signup Funnel" testId="section-funnel"
        icon={Filter}
        desc="Seller-activation path — each step is a subset of the one above it, so a step can never exceed the previous."
        scope="All-time · Site Visit → Signup → First Listing → First Sale">
        {ua && (
          <>
            <div className="border border-sniper bg-surface p-4 space-y-3" data-testid="signup-funnel">
              {(ua.signup_funnel || []).map((s) => (
                <div key={s.stage} data-testid={`funnel-${s.stage.replace(/\s+/g, "-").toLowerCase()}`}>
                  <div className="flex items-center justify-between font-mono text-xs mb-1">
                    <span className="text-secondary-foreground uppercase">{s.stage}</span>
                    <span className="text-foreground">
                      {s.not_measured ? (
                        <span className="text-[#d3a24e] uppercase" data-testid={`funnel-none-${s.stage.replace(/\s+/g, "-").toLowerCase()}`}>None yet</span>
                      ) : s.exceeds_prev ? (
                        <span className="text-blaze inline-flex items-center gap-1" data-testid="funnel-warning"><AlertTriangle className="w-3 h-3" /> exceeds previous step — count under review</span>
                      ) : (
                        <>{s.count}<span className="text-muted-foreground"> · {s.pct_of_top}% of top{s.pct_of_prev != null ? ` · ${s.pct_of_prev}% of prev` : ""}</span></>
                      )}
                    </span>
                  </div>
                  <div className="h-3 bg-obsidian">
                    <div className="h-full bg-gradient-to-r from-olive to-olive-bright" style={{ width: `${Math.max(s.pct_of_top || 0, 1)}%` }} />
                  </div>
                </div>
              ))}
            </div>
            {ua.engagement_first_message && (
              <div className="mt-3 border border-sniper bg-surface p-3 flex items-start gap-2" data-testid="engagement-first-message">
                <MessageSquarePlus className="w-4 h-4 text-olive-bright mt-0.5 shrink-0" />
                <p className="font-mono text-[11px] text-secondary-foreground leading-relaxed">
                  <span className="text-foreground">First message: {ua.engagement_first_message.count}</span> — {ua.engagement_first_message.note}
                </p>
              </div>
            )}
          </>
        )}
      </Section>

      {/* 7 — USERS */}
      <Section title="Users" testId="section-users"
        icon={Users}
        desc="Registered base and unique active users. Individual members are managed on the Users page, not profiled here."
        scope="Reuses SITREP activity heartbeat">
        {ua && (
          <>
            <div className="flex justify-end mb-3">
              <ExportButton path="/admin/analytics/export/users.csv" filename="opticswap_users.csv" testId="export-users" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ua-totals">
              <CountTile icon={Users} label="Total Registered" value={ua.totals?.total_users} />
              <CountTile icon={Gauge} label="Daily Active Users" value={ua.active_users?.dau} />
              <CountTile icon={Gauge} label="Weekly Active Users" value={ua.active_users?.wau} />
              <CountTile icon={Gauge} label="Monthly Active Users" value={ua.active_users?.mau} />
            </div>
            <h3 className="font-heading text-xl uppercase tracking-tight mt-8 mb-3 text-foreground flex items-center gap-2"><MapPin className="w-4 h-4 text-olive-bright" /> User Geography</h3>
            <p className="font-mono text-[10px] text-muted-foreground mb-2">Self-reported state — aggregate counts only, identifies nobody.</p>
            <BarList testId="user-geo" rows={(ua.user_geo || []).map((g) => ({ label: g.state, value: g.count }))} />
          </>
        )}
      </Section>

      {/* 8 — COHORT MATRIX */}
      <Section title="Cohort Matrix" testId="section-cohorts"
        icon={Table2}
        desc="Share of each signup cohort that reached a milestone, by time since signup.">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <span className="font-mono text-[11px] uppercase tracking-wide text-secondary-foreground" data-testid="cohort-denominators">
            Cohorts: {cohortCount} · Total users: {cohortTotalUsers}
          </span>
          <div className="inline-flex border border-sniper bg-surface p-1">
            {[["first_listing", "First Listing"], ["first_sale", "First Sale"]].map(([v, l]) => (
              <button key={v} onClick={() => setCohortMetric(v)} data-testid={`cohort-metric-${v}`}
                className={`px-4 py-1.5 font-mono uppercase text-[11px] tracking-wide transition-colors ${cohortMetric === v ? "bg-olive text-white" : "text-secondary-foreground hover:text-white"}`}>
                {l}
              </button>
            ))}
          </div>
        </div>
        <div className="border border-olive/30 bg-olive/5 p-3 mb-3 text-[11px] font-mono text-secondary-foreground leading-relaxed" data-testid="cohort-help">
          <span className="text-olive-bright uppercase font-bold">How to read this: </span>
          Each row is a group of users who signed up in the same month (a "cohort"). Each column is time
          <span className="text-foreground"> since signup</span> — not a calendar month — so every cohort is compared on the same
          footing (a brand-new cohort and an older one both start at "Signup month"). Each cell shows the share of that cohort
          who reached their {cohortMetric === "first_sale" ? "first sale" : "first listing"} by that point. Blank cells mean that
          much time hasn't passed for that cohort yet. Cohorts marked <span className="text-blaze">⚠ low sample</span> have fewer
          than 5 users, so one person can swing the percentage — read those with caution.
        </div>
        <div className="border border-sniper bg-surface overflow-x-auto" data-testid="cohort-matrix">
          <table className="w-full text-sm">
            <thead>
              <tr className="font-mono text-[10px] uppercase text-muted-foreground border-b border-sniper">
                <th className="text-left p-3">Signup cohort</th>
                <th className="text-right p-3">Cohort size</th>
                {(cohorts?.offsets || []).map((o, i) => <th key={o} className="text-center p-3 whitespace-nowrap">{offsetLabel(i)}</th>)}
              </tr>
            </thead>
            <tbody>
              {(cohorts?.rows || []).map((row) => (
                <tr key={row.cohort} className="border-b border-sniper last:border-0" data-testid={`cohort-row-${row.cohort}`}>
                  <td className="p-3 font-body text-foreground whitespace-nowrap">
                    {row.label}
                    {row.size < 5 && <span className="ml-2 font-mono text-[10px] uppercase text-blaze" title="Fewer than 5 users — small sample" data-testid={`cohort-lowsample-${row.cohort}`}>⚠ low sample</span>}
                  </td>
                  <td className="p-3 text-right font-mono text-secondary-foreground">{row.size}</td>
                  {row.values.map((v, i) => (
                    <td key={i} className="p-0 text-center">
                      <div className="m-1 py-2.5 font-mono text-xs" style={{ background: heatBg(v), color: v == null ? "#5C605C" : (v > 45 ? "#0A0A0A" : "#E6E8E3") }}>
                        {v == null ? "" : `${v}%`}
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* intensity legend */}
        <div className="flex items-center gap-3 mt-3" data-testid="cohort-legend">
          <span className="font-mono text-[10px] uppercase text-muted-foreground">Low</span>
          <div className="flex-1 max-w-[240px] h-3 flex">
            {[0, 20, 40, 60, 80, 100].map((p) => <div key={p} className="flex-1" style={{ background: heatBg(p) }} />)}
          </div>
          <span className="font-mono text-[10px] uppercase text-muted-foreground">High</span>
          <span className="font-mono text-[10px] text-muted-foreground ml-2">% of cohort reached milestone</span>
        </div>
      </Section>

      {/* 9 — BETA PULSE + CANCELLATIONS */}
      <Section title="Beta Pulse" testId="section-beta-pulse"
        icon={Radio}
        desc="Beta program health — signups, feedback and waitlist — plus this month's cancellations."
        scope="Beta program">
        {pulse && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="beta-pulse">
            <div className="border border-sniper bg-obsidian p-4" data-testid="pulse-testers">
              <span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1"><UserPlus className="w-3.5 h-3.5" /> Active Testers</span>
              <div className="font-heading text-3xl mt-2 text-foreground">{pulse.signups_30d}<span className="text-xs font-mono text-muted-foreground"> / 30 days</span></div>
              <div className="font-mono text-[11px] text-olive-bright mt-0.5">{pulse.signups_7d} in last 7 days</div>
            </div>
            <div className="border border-sniper bg-obsidian p-4" data-testid="pulse-feedback">
              <span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1"><MessageSquarePlus className="w-3.5 h-3.5" /> Feedback</span>
              <div className="font-heading text-3xl mt-2 text-foreground">{pulse.feedback_total}</div>
              <div className="font-mono text-[11px] text-blaze mt-0.5">{pulse.feedback_unresolved} unresolved</div>
            </div>
            <div className="border border-sniper bg-obsidian p-4" data-testid="pulse-waitlist">
              <span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1"><ListChecks className="w-3.5 h-3.5" /> Waitlist</span>
              <div className="font-heading text-3xl mt-2 text-foreground">{pulse.waitlist_total}</div>
              <div className="font-mono text-[11px] text-olive-bright mt-0.5">+{pulse.waitlist_growth_30d} in last 30 days</div>
            </div>
            <div className="border border-sniper bg-obsidian p-4" data-testid="pulse-categories">
              <span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">Feedback by Type</span>
              <div className="flex flex-wrap gap-1.5 mt-2.5">
                {Object.keys(pulse.feedback_by_category).length === 0 && <span className="font-mono text-xs text-muted-foreground">No feedback yet</span>}
                {Object.entries(pulse.feedback_by_category).map(([cat, n]) => (
                  <span key={cat} className="font-mono text-[11px] uppercase border border-olive/40 text-olive-bright px-1.5 py-0.5">{cat} {n}</span>
                ))}
              </div>
            </div>
          </div>
        )}
        <CancellationsCard />
      </Section>

      <div className="h-10" />
    </div>
  );
}

function CancellationsCard() {
  const [m, setM] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    api.get("/admin/cancellations/metrics").then(({ data }) => { setM(data); setErr(false); }).catch(() => setErr(true));
  }, []);
  const noPaid = m && (m.paid_orders === 0 || m.cancellation_rate_pct == null);
  return (
    <div className="border border-sniper bg-surface mt-6" data-testid="analytics-cancellations">
      <div className="px-4 py-3 border-b border-sniper flex items-center gap-2">
        <XCircle className="w-4 h-4 text-blaze" />
        <h3 className="font-heading text-xl uppercase tracking-tight text-foreground">Cancellations · this month</h3>
      </div>
      {err && !m && <p className="p-4 font-mono text-xs text-blaze">Could not load cancellation metrics.</p>}
      {m && (
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 font-mono text-xs">
            <div className="border border-sniper p-2.5" data-testid="cancel-rate">
              <span className="text-muted-foreground block text-[10px] uppercase">Rate (of paid)</span>
              {noPaid ? (
                <span className="text-muted-foreground text-base flex flex-col leading-tight" data-testid="cancel-rate-nopaid">
                  <span>—</span><span className="text-[9px] uppercase tracking-wide">No paid orders yet</span>
                </span>
              ) : (
                <span className="text-foreground text-base">{m.cancellation_rate_pct}%</span>
              )}
            </div>
            <div className="border border-sniper p-2.5" data-testid="cancel-total"><span className="text-muted-foreground block text-[10px] uppercase">Seller cancels</span><span className="text-foreground text-base">{m.seller_cancellations}</span></div>
            <div className="border border-sniper p-2.5" data-testid="cancel-auto"><span className="text-muted-foreground block text-[10px] uppercase">Auto (SLA)</span><span className="text-foreground text-base">{m.auto_sla}</span></div>
            <div className="border border-sniper p-2.5" data-testid="cancel-manual"><span className="text-muted-foreground block text-[10px] uppercase">Manual</span><span className="text-foreground text-base">{m.manual}</span></div>
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">By reason</p>
            {(m.by_reason || []).length === 0 ? (
              <p className="font-mono text-[11px] text-muted-foreground">None this month.</p>
            ) : (
              <ul className="space-y-1 font-mono text-[11px]" data-testid="cancel-by-reason">
                {m.by_reason.map((r) => (
                  <li key={r.reason} className="flex justify-between text-secondary-foreground"><span className="text-foreground">{r.reason}</span><span>{r.count}</span></li>
                ))}
              </ul>
            )}
          </div>
          <p className="font-body text-[11px] text-muted-foreground">Buyer-requested cancels this month (no strike): {m.buyer_requested}.</p>
        </div>
      )}
    </div>
  );
}
